/**
 * Public client intake form (bilingual)
 */

import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from '../hooks/useTranslation'
import { useLanguage } from '../contexts/LanguageContext'
import { usePageSeo } from '../hooks/usePageSeo'
import { useTurnstile } from '../hooks/useTurnstile'
import TurnstileWidget from '../components/TurnstileWidget'
import MarketingConsentField from '../components/MarketingConsentField'
import { TURNSTILE_ACTIONS } from '../constants/turnstile'
import { withTurnstileToken } from '../utils/scheduler/turnstile'
import { Button } from '@/components/ui/button'

const SERVICE_OPTIONS = [
  'individual_counseling',
  'family_couples',
  'support_group',
  'professional_consultation',
]

const emptyPerson = () => ({
  first_name: '',
  last_name: '',
  dob: '',
  email: '',
  phone: '',
})

export default function IntakeForm() {
  const t = useTranslation()
  const { isLoading, language } = useLanguage()
  const location = useLocation()

  usePageSeo({
    enabled: !isLoading,
    title: t('intake.meta.title'),
    description: t('intake.meta.description'),
    keywords: t('intake.meta.keywords'),
    pathname: location.pathname,
  })

  const [clientType, setClientType] = useState('adult')
  const [serviceType, setServiceType] = useState('individual_counseling')
  const [adult, setAdult] = useState(emptyPerson())
  const [person1, setPerson1] = useState(emptyPerson())
  const [person2, setPerson2] = useState(emptyPerson())
  const [minor, setMinor] = useState({
    guardian_name: '',
    guardian_phone: '',
    guardian_email: '',
    minor_full_name: '',
    minor_dob: '',
  })
  const [marketingConsent, setMarketingConsent] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')
  const turnstile = useTurnstile()

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-green-600 focus:border-green-600'

  const buildPayload = () => {
    const base = {
      client_type: clientType,
      service_type: serviceType,
      language,
      marketing_consent: marketingConsent,
    }
    if (clientType === 'adult') return { ...base, adult }
    if (clientType === 'minor') return { ...base, minor }
    return { ...base, person1, person2 }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (turnstile.isEnabled && !turnstile.token) {
      setError(t('intake.turnstile.required'))
      return
    }

    setSubmitting(true)
    try {
      const res = await fetch('/api/intake/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(withTurnstileToken(buildPayload(), turnstile.token)),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.success) {
        const message =
          res.status === 403
            ? t('intake.turnstile.failed')
            : data.error || t('intake.errorGeneric')
        throw new Error(message)
      }
      setSubmitted(true)
    } catch (err) {
      setError(err.message || t('intake.errorGeneric'))
      turnstile.reset()
    } finally {
      setSubmitting(false)
    }
  }

  const renderPersonFields = (person, setPerson, prefix = '') => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('intake.fields.firstName')}
        </label>
        <input
          type="text"
          required
          className={inputClass}
          value={person.first_name}
          onChange={(e) => setPerson((p) => ({ ...p, first_name: e.target.value }))}
          autoComplete={prefix ? `${prefix}-given-name` : 'given-name'}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('intake.fields.lastName')}
        </label>
        <input
          type="text"
          required
          className={inputClass}
          value={person.last_name}
          onChange={(e) => setPerson((p) => ({ ...p, last_name: e.target.value }))}
          autoComplete={prefix ? `${prefix}-family-name` : 'family-name'}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('intake.fields.dob')}
        </label>
        <input
          type="date"
          required
          className={inputClass}
          value={person.dob}
          onChange={(e) => setPerson((p) => ({ ...p, dob: e.target.value }))}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('intake.fields.phone')}
        </label>
        <input
          type="tel"
          required
          className={inputClass}
          value={person.phone}
          onChange={(e) => setPerson((p) => ({ ...p, phone: e.target.value }))}
          autoComplete={prefix ? `${prefix}-tel` : 'tel'}
        />
      </div>
      <div className="sm:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('intake.fields.email')}
        </label>
        <input
          type="email"
          required
          className={inputClass}
          value={person.email}
          onChange={(e) => setPerson((p) => ({ ...p, email: e.target.value }))}
          autoComplete={prefix ? `${prefix}-email` : 'email'}
        />
      </div>
    </div>
  )

  return (
    <div className="bg-gray-50 min-h-[60vh] py-10 sm:py-14">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('intake.title')}</h1>
            <p className="mt-2 text-gray-600">{t('intake.subtitle')}</p>
          </div>

          {submitted ? (
            <div className="bg-white rounded-lg shadow p-6 sm:p-8 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900">{t('intake.successTitle')}</h2>
              <p className="mt-2 text-gray-600">{t('intake.successMessage')}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 sm:p-8 space-y-8">
              <fieldset>
                <legend className="text-sm font-medium text-gray-900 mb-3">
                  {t('intake.clientType.label')}
                </legend>
                <div className="flex flex-wrap gap-4">
                  {['adult', 'minor', 'couple'].map((type) => (
                    <label key={type} className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="clientType"
                        value={type}
                        checked={clientType === type}
                        onChange={() => setClientType(type)}
                        className="text-green-600 focus:ring-green-600"
                      />
                      <span className="text-sm text-gray-800">{t(`intake.clientType.${type}`)}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  {t('intake.service.label')}
                </label>
                <select
                  required
                  className={inputClass}
                  value={serviceType}
                  onChange={(e) => setServiceType(e.target.value)}
                >
                  {SERVICE_OPTIONS.map((key) => (
                    <option key={key} value={key}>
                      {t(`intake.service.${key}`)}
                    </option>
                  ))}
                </select>
              </div>

              {clientType === 'adult' && renderPersonFields(adult, setAdult)}

              {clientType === 'minor' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('intake.fields.guardianName')}
                      </label>
                      <input
                        type="text"
                        required
                        className={inputClass}
                        value={minor.guardian_name}
                        onChange={(e) => setMinor((m) => ({ ...m, guardian_name: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('intake.fields.guardianPhone')}
                      </label>
                      <input
                        type="tel"
                        required
                        className={inputClass}
                        value={minor.guardian_phone}
                        onChange={(e) => setMinor((m) => ({ ...m, guardian_phone: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('intake.fields.guardianEmail')}
                      </label>
                      <input
                        type="email"
                        required
                        className={inputClass}
                        value={minor.guardian_email}
                        onChange={(e) => setMinor((m) => ({ ...m, guardian_email: e.target.value }))}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('intake.fields.minorFullName')}
                      </label>
                      <input
                        type="text"
                        required
                        className={inputClass}
                        value={minor.minor_full_name}
                        onChange={(e) => setMinor((m) => ({ ...m, minor_full_name: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('intake.fields.minorDob')}
                      </label>
                      <input
                        type="date"
                        required
                        className={inputClass}
                        value={minor.minor_dob}
                        onChange={(e) => setMinor((m) => ({ ...m, minor_dob: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              )}

              {clientType === 'couple' && (
                <div className="space-y-8">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900 mb-3 border-b border-gray-200 pb-2">
                      {t('intake.fields.person1')}
                    </h2>
                    {renderPersonFields(person1, setPerson1, 'p1')}
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900 mb-3 border-b border-gray-200 pb-2">
                      {t('intake.fields.person2')}
                    </h2>
                    {renderPersonFields(person2, setPerson2, 'p2')}
                  </div>
                </div>
              )}

              <MarketingConsentField
                id="intake-marketing-consent"
                checked={marketingConsent}
                onChange={setMarketingConsent}
              />

              <div
                className="intake-turnstile-host"
                role="group"
                aria-labelledby="intake-turnstile-label"
              >
                <p
                  id="intake-turnstile-label"
                  className="mb-3 text-center text-sm font-medium text-gray-600"
                >
                  {t('intake.turnstile.securityVerification')}
                </p>
                <TurnstileWidget
                  action={TURNSTILE_ACTIONS.INTAKE_FORM}
                  resetSignal={turnstile.resetSignal}
                  onTokenChange={turnstile.setToken}
                  onError={turnstile.clearToken}
                />
              </div>

              {error && (
                <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
                  {error}
                </div>
              )}

              <div className="w-full border-t border-gray-200 pt-6 text-center">
                <Button
                  type="submit"
                  variant="brand"
                  size="lg"
                  disabled={submitting || !turnstile.canSubmit}
                  className="px-10"
                >
                  {submitting ? t('intake.submitting') : t('intake.submit')}
                </Button>
              </div>
            </form>
          )}
        </div>
    </div>
  )
}
